import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Sparkles, RefreshCw, Crown, Sword, Users, UsersRound,
  AlertTriangle, Save, Calendar
} from 'lucide-react';
import { specialEventsService } from '../../services';
import { useNotification } from '../../contexts/NotificationContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import {
  characterClasses,
  getClassTypeCollor,
  getClassCollor,
  getClassTypeIcon,
} from '../../utils/characterTranslation';

const MAX_TOTAL = 65;
const MAX_PARTY_SIZE = 5;

// Quantidades padrão por classe (alteráveis antes de gerar)
const DEFAULT_COUNTS = {
  LUTADOR: 33,
  ARQUEIRA: 13,
  MECANICO: 10,
  SACERDOTISA: 3,
  ATALANTA: 2,
  CAVALEIRO: 2,
  PIKE: 2,
};

const initialCounts = characterClasses.reduce((acc, c) => {
  acc[c.value] = DEFAULT_COUNTS[c.value] ?? 0;
  return acc;
}, {});

// Lista as relíquias principais que um membro possui
const memberRelics = (member) => {
  const relics = [];
  if (member.hasMidranda) relics.push('Midranda');
  if (member.hasYag) relics.push('Yag');
  if (member.hasDraxos) relics.push('Draxos');
  if (member.hasBabel) relics.push('Babel');
  return relics;
};

// Card read-only de um grupo do Dream Team (mesmo visual dos grupos do evento)
const DreamTeamPartyCard = ({ party }) => {
  const duplicates = party.stats?.duplicateClasses || [];

  return (
    <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 backdrop-blur-xl rounded-2xl border border-white/10 p-4 hover:border-orange-500/50 transition-colors">
      <div className="space-y-3">
        {/* Header do grupo */}
        <div className="flex justify-between items-center pb-3 border-b border-white/10">
          <div>
            <h3 className="text-lg font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">
              Grupo {String(party.partyNumber).padStart(2, '0')}
            </h3>
            <div className="flex flex-wrap gap-3 text-sm text-gray-400 mt-1">
              <span className={party.members.length > MAX_PARTY_SIZE ? 'text-red-400 font-semibold' : ''}>
                {party.members.length}/{MAX_PARTY_SIZE} membros
              </span>
              <span>•</span>
              <span>Lv médio {party.stats?.averageLevel ?? 0}</span>
            </div>
          </div>
        </div>

        {/* Membros */}
        <div className="space-y-2">
          {party.members.map((member, idx) => {
            const relics = memberRelics(member);
            const isLeader = member.id === party.leaderId;
            return (
              <div
                key={member.id}
                className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10"
              >
                <div className="flex-shrink-0">
                  <Sword className="w-4 h-4 text-gray-400" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-white font-medium truncate">
                      {String(idx + 1).padStart(2, '0')}. {member.name}
                    </p>
                    <span className="text-gray-500">→</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getClassTypeCollor(member.class)}`}>
                      {getClassTypeIcon(member.class)} {member.class}
                    </span>
                  </div>
                  <p className="text-gray-500 text-xs mt-0.5">
                    Nível {member.level}
                  </p>
                  {relics.length > 0 && (
                    <p className={`font-bold text-xs mt-0.5 ${getClassCollor(member.class)}`}>
                      {relics.join(' • ')}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const DreamTeamBuilder = () => {
  const navigate = useNavigate();
  const { notify } = useNotification();

  const [counts, setCounts] = useState(initialCounts);
  const [generating, setGenerating] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const [result, setResult] = useState(null); // { parties, stats, warnings }

  const [eventForm, setEventForm] = useState({
    name: '',
    eventType: 'PVP',
    eventDate: '',
  });

  const totalRequested = Object.values(counts).reduce((sum, n) => sum + (parseInt(n, 10) || 0), 0);
  const overLimit = totalRequested > MAX_TOTAL;

  const handleCountChange = (classValue, value) => {
    const num = Math.max(0, parseInt(value, 10) || 0);
    setCounts(prev => ({ ...prev, [classValue]: num }));
  };

  const handleReset = () => {
    setCounts(initialCounts);
    setResult(null);
    setError('');
  };

  const handleGenerate = async () => {
    if (totalRequested === 0) {
      setError('Informe a quantidade de pelo menos uma classe.');
      return;
    }

    // Envia apenas classes com quantidade > 0
    const classCounts = Object.entries(counts)
      .filter(([, n]) => n > 0)
      .reduce((acc, [cls, n]) => ({ ...acc, [cls]: n }), {});

    try {
      setGenerating(true);
      setError('');
      const data = await specialEventsService.dreamTeamPreview(classCounts);
      setResult(data);
      if (!data.parties || data.parties.length === 0) {
        setError('Nenhum personagem encontrado para as classes informadas.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao gerar o Dream Team');
    } finally {
      setGenerating(false);
    }
  };

  const handleCreateEvent = async () => {
    if (!eventForm.name.trim()) {
      setError('Informe o nome do evento.');
      return;
    }
    if (!result?.parties?.length) {
      setError('Gere o Dream Team antes de criar o evento.');
      return;
    }

    const payload = {
      name: eventForm.name.trim(),
      eventType: eventForm.eventType,
      eventDate: eventForm.eventDate || null,
      parties: result.parties.map(party => ({
        partyNumber: party.partyNumber,
        partyName: party.partyName,
        leaderCharacterId: party.leaderId,
        members: party.members.map(m => m.id),
      })),
    };

    try {
      setCreating(true);
      setError('');
      const res = await specialEventsService.dreamTeamCreate(payload);
      notify.success('Evento criado a partir do Dream Team!', 'Evento Criado');
      navigate(`/app/admin/special-events/${res.event.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao criar o evento');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/app/admin/special-events')}
              className="flex items-center justify-center w-10 h-10 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-orange-500/50 rounded-lg text-gray-300 hover:text-white transition-all"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent mb-1 flex items-center gap-2">
                <Sparkles className="w-8 h-8 text-orange-400" />
                Dream Team
              </h1>
              <p className="text-gray-300">
                Monte o time de ataque dos sonhos escolhendo a quantidade de cada classe
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/50 rounded-2xl p-4">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Configuração de classes */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <h2 className="text-xl font-bold text-white">Quantidade por classe</h2>
            <div className="flex items-center gap-3">
              <span className={`text-sm font-semibold ${overLimit ? 'text-red-400' : 'text-gray-300'}`}>
                Total: {totalRequested}/{MAX_TOTAL}
              </span>
              <span className="text-xs text-gray-500">
                Máx. {MAX_PARTY_SIZE} por grupo • 1 de cada classe por grupo
              </span>
            </div>
          </div>

          {overLimit && (
            <div className="flex items-center gap-2 text-sm text-yellow-400 bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-3 py-2 mb-4">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>O total ultrapassa {MAX_TOTAL}. Os membros de menor pontuação serão removidos automaticamente.</span>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {characterClasses.map(c => (
              <div key={c.value} className="bg-white/5 rounded-xl border border-white/10 p-3">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  {c.emoji} {c.label}
                </label>
                <Input
                  type="number"
                  min="0"
                  value={counts[c.value]}
                  onChange={(e) => handleCountChange(c.value, e.target.value)}
                  className="text-sm"
                />
              </div>
            ))}
          </div>

          <div className="flex gap-3 pt-5 mt-5 border-t border-white/10">
            <Button
              onClick={handleReset}
              className="bg-white/5 hover:bg-white/10 border border-white/10"
            >
              Limpar
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={generating || totalRequested === 0}
              className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 shadow-lg shadow-orange-500/20"
            >
              <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
              {generating ? 'Gerando...' : 'Gerar Dream Team'}
            </Button>
          </div>
        </div>

        {/* Resultado */}
        {result?.parties?.length > 0 && (
          <>
            {/* Estatísticas */}
            <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2 text-gray-300">
                  <Users className="w-5 h-5 text-orange-400" />
                  <span className="font-semibold text-white">{result.stats.totalMembers}</span> membros
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <UsersRound className="w-5 h-5 text-orange-400" />
                  <span className="font-semibold text-white">{result.stats.totalParties}</span> grupos
                </div>
              </div>

              {result.warnings?.length > 0 && (
                <div className="mt-4 space-y-1">
                  {result.warnings.map((w, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-yellow-400">
                      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{w}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Grupos */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {result.parties.map(party => (
                <DreamTeamPartyCard key={party.partyNumber} party={party} />
              ))}
            </div>

            {/* Criar evento */}
            <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-orange-400" />
                Criar evento especial com este Dream Team
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Nome *</label>
                  <Input
                    value={eventForm.name}
                    onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })}
                    placeholder="Ex: Dream Team PVP - 01/07"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Tipo *</label>
                  <Select
                    value={eventForm.eventType}
                    onChange={(e) => setEventForm({ ...eventForm, eventType: e.target.value })}
                  >
                    <option value="PVP">PVP</option>
                    <option value="BC">BC</option>
                    <option value="RAID">Raid</option>
                    <option value="DUNGEON">Dungeon</option>
                    <option value="CUSTOM">Personalizado</option>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Data</label>
                  <Input
                    type="date"
                    value={eventForm.eventDate}
                    onChange={(e) => setEventForm({ ...eventForm, eventDate: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex justify-end pt-5 mt-5 border-t border-white/10">
                <Button
                  onClick={handleCreateEvent}
                  disabled={creating || !eventForm.name.trim()}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 shadow-lg shadow-green-500/20"
                >
                  <Save className="w-4 h-4" />
                  {creating ? 'Criando...' : 'Criar Evento'}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DreamTeamBuilder;
